/** Stable fallback so `useEffect` deps do not see a new object every render when socket map is absent. */
export const EMPTY_BY_PRODUCT_ID = {};

export const extractionText = (v) => {
	if (v == null) return '';
	if (typeof v === 'string') return v.trim();
	if (typeof v !== 'object') return String(v).trim();
	for (const key of ['value', 'name', 'text', 'label']) {
		if (typeof v[key] === 'string') return v[key].trim();
	}
	return '';
};

export const normalizeFileName = (name) =>
	String(name || '')
		.trim()
		.toLowerCase()
		.replace(/\s+/g, ' ');

export const isPdfFile = (file) =>
	file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');

export const isImageFile = (file) => {
	if (file.type === 'image/jpeg' || file.type === 'image/png') {
		return true;
	}
	return /\.(jpe?g|png)$/i.test(file.name);
};

export const isAllowedTechpackFile = (file) => isPdfFile(file) || isImageFile(file);

export const docMimeTypeForFile = (file) => {
	if (file.type) {
		return file.type;
	}
	const ext = file.name.split('.').pop()?.toLowerCase();
	const byExt = {
		pdf: 'application/pdf',
		jpg: 'image/jpeg',
		jpeg: 'image/jpeg',
		png: 'image/png'
	};
	return byExt[ext] || 'application/octet-stream';
};

export const findListMatch = (list, raw, idKey, labelKey) => {
	if (!list?.length) return null;

	// 1) If incoming value is an object, first try ID based exact match.
	const isObjectInput = raw && typeof raw === 'object' && !Array.isArray(raw);
	const rawId = isObjectInput ? (raw[idKey] ?? raw.id ?? raw.code) : null;
	if (rawId != null && rawId !== '') {
		const exactIdMatch = list.find((item) => String(item[idKey]) === String(rawId));
		if (exactIdMatch) {
			return { id: exactIdMatch[idKey], value: exactIdMatch[labelKey] };
		}
	}

	// 2) Fallback to label based text matching.
	const normalizedInput = extractionText(raw).toLowerCase();
	if (!normalizedInput) return null;

	const normalizedLabel = (item) => (item[labelKey] ?? '').toString().trim().toLowerCase();
	const exactLabelMatch = list.find((item) => normalizedLabel(item) === normalizedInput);
	const partialLabelMatch =
		exactLabelMatch || list.find((item) => normalizedLabel(item).includes(normalizedInput));

	if (!partialLabelMatch) return null;
	return { id: partialLabelMatch[idKey], value: partialLabelMatch[labelKey] };
};

/**
 * Techpack list row: copy when a field has no value (not extractionData keys —
 * those are e.g. buyer_design_no, print, bodyFabric).
 */
export const TP_ROW_EMPTY_LABEL = {
	buyerDesignNo: 'Buyer design No',
	market: 'Market',
	category: 'Category',
	print: 'Print',
	embroidery: 'Embroidery',
	wash: 'Wash',
	fabricType: 'Fabric type',
	composition: 'Composition',
	gsm: 'GSM'
};

/** `fabricType` may be a string (`"KNIT"`) or `{ name }` like `fiber`; also `fabric_type`. */
const extractCompositionRowFabricType = (row) => {
	const ft = row?.fabricType ?? row?.fabric_type;
	if (ft == null || ft === '') return '';
	if (typeof ft === 'string' || typeof ft === 'number') return extractionText(String(ft));
	return extractionText(ft?.name ?? ft);
};

/** Builds a display string from `bodyFabric.composition` (fiber + % only; fabricType is not shown here). */
export const formatCompositionFromBodyFabric = (bodyFabric) => {
	if (!bodyFabric || typeof bodyFabric !== 'object') return '';
	const arr = bodyFabric.composition;
	if (!Array.isArray(arr) || arr.length === 0) return '';
	const parts = arr
		.map((row) => {
			const fiberName = extractionText(row?.fiber?.name ?? row?.fiber);
			const pct = row?.percentage != null ? String(row.percentage).trim() : '';
			if (!fiberName) return '';
			return pct ? `${pct}% ${fiberName} ` : fiberName;
		})
		.filter(Boolean);
	return parts.join(', ');
};

/** Parse "60% Cotton, 40% Polyester" into bodyFabric composition rows. */
export const compositionRowsFromText = (rawText, limit = 4) => {
	const text = extractionText(rawText);
	if (!text) return [];
	return text
		.split(',')
		.map((chunk) => chunk.trim())
		.filter(Boolean)
		.slice(0, limit)
		.map((chunk) => {
			const match = chunk.match(/^(\d+(?:\.\d+)?)\s*%?\s*(.*)$/);
			if (!match) {
				return { fiber: { id: '', name: chunk }, percentage: '' };
			}
			const percentageNum = Number(match[1]);
			const percentage = Number.isFinite(percentageNum) ? String(percentageNum) : '';
			const fiberName = String(match[2] || '').trim() || chunk;
			return { fiber: { id: '', name: fiberName }, percentage };
		});
};

export const parseFabricationDropdown = (rawValue) => {
	const parts = extractionText(rawValue)
		.split(',')
		.map((s) => s.trim());
	return {
		fabricType: extractionText(parts[0]),
		composition: extractionText(parts[1]),
		gsm: extractionText(parts[2])
	};
};

export const firstFabricTypeFromBodyComposition = (bodyFabric) => {
	if (!bodyFabric?.composition?.length) return '';
	for (const row of bodyFabric.composition) {
		const t = extractCompositionRowFabricType(row);
		if (t) return t;
	}
	return '';
};

//get all data from Add Fabric Composition Modal

export const getFabricationRawFields = (extractionData) => {
	if (!extractionData || typeof extractionData !== 'object') {
		return {
			fabricTypeRaw: undefined,
			compositionValue: undefined,
			gsmValue: undefined,
			constructionValue: undefined,
			constructionId: undefined,
			fabricTypeConstant: undefined
		};
	}
	const dropdownLine = String(extractionData['Fabric type'] ?? '')
		.split(',')
		.map((s) => s.trim());
	const dropdownConstruction = extractionText(dropdownLine[0]);
	const dropdownComposition = extractionText(dropdownLine[1]);
	const dropdownGsm = extractionText(dropdownLine[2]);
	const bodyFabricRow = extractionData['bodyFabric'];
	const constructionRef = bodyFabricRow?.construction;
	const constructionPrimary =
		constructionRef != null && typeof constructionRef === 'object'
			? (constructionRef.name ?? constructionRef.label ?? '')
			: constructionRef;
	const constructionValue =
		extractionText(constructionPrimary) ||
		dropdownConstruction ||
		extractionText(extractionData.fabricType);
	const constructionId =
		bodyFabricRow?.constructionId ??
		(constructionRef != null && typeof constructionRef === 'object' ? constructionRef.id : undefined);
	const fabricTypeRaw =
		extractionData.fabricType ??
		(firstFabricTypeFromBodyComposition(bodyFabricRow) || undefined) ??
		constructionValue ??
		extractionData['Fabric type'];
	const compositionPrimary =
		extractionData['Composition (%)'] ??
		extractionData['Composition'] ??
		formatCompositionFromBodyFabric(bodyFabricRow);
	const compositionValue = extractionText(compositionPrimary) || dropdownComposition;
	const gsmPrimary =
		extractionData['GSM/weight'] ??
		extractionData['GSM'] ??
		extractionData['gsm'] ??
		(bodyFabricRow?.gsm != null ? String(bodyFabricRow.gsm).trim() : '');
	const gsmValue = extractionText(gsmPrimary) || dropdownGsm;
	const fabricTypeConstant =
		extractionData.fabricTypeConstant ??
		(bodyFabricRow != null && typeof bodyFabricRow === 'object'
			? bodyFabricRow.fabricTypeConstant
			: undefined);
	return {
		fabricTypeRaw,
		compositionValue,
		gsmValue,
		constructionValue,
		constructionId,
		fabricTypeConstant
	};
};

/** Request body for POST `/material/search-or-add` from merged extractionData (incl. `bodyFabric`). */
export const buildMaterialSearchOrAddBody = (extractionData) => {
	const ed = extractionData && typeof extractionData === 'object' ? extractionData : {};
	const bodyFabric = ed.bodyFabric && typeof ed.bodyFabric === 'object' ? ed.bodyFabric : {};

	const constructionRef = bodyFabric.construction;
	let construction = bodyFabric.constructionId;
	if (construction == null && constructionRef != null) {
		construction = typeof constructionRef === 'object' ? constructionRef.id : constructionRef;
	}
	const constructionNum = Number(construction);
	const constructionOut = Number.isFinite(constructionNum) ? constructionNum : 0;

	const gsmRaw = ed['GSM/weight'] ?? ed['GSM'] ?? ed.gsm ?? bodyFabric.gsm;
	const gsmNum = Number(gsmRaw);
	const gsmOut = Number.isFinite(gsmNum) ? gsmNum : 0;

	const compArr = Array.isArray(bodyFabric.composition) ? bodyFabric.composition : [];
	const composition = compArr.map((row) => {
		const fiber = row?.fiber;
		let id = 0;
		if (fiber != null && typeof fiber === 'object') {
			const nid = Number(fiber.id);
			id = Number.isFinite(nid) ? nid : 0;
		}
		const fiberName =
			fiber == null
				? ''
				: typeof fiber === 'object'
					? String(fiber.name ?? fiber.value ?? '').trim()
					: String(fiber).trim();
		const pct = Number(row?.percentage);
		return {
			id,
			percentage: Number.isFinite(pct) ? pct : 0,
			fiberName
		};
	});

	return { construction: constructionOut, gsm: gsmOut, composition };
};

/** Construction + composition + GSM for the selected row only (Fabrication dropdown hint). */
export const buildSelectedFabricInfoValue = (extractionData) => {
	const { constructionValue, compositionValue, gsmValue } = getFabricationRawFields(extractionData);
	const constructionText = extractionText(constructionValue);
	const compT = extractionText(compositionValue);
	const gsmT = extractionText(gsmValue);
	if (!constructionText || !compT || !gsmT) return '';
	return [constructionText, compT, gsmT].join(', ');
};

/** Merge Add-fabric-modal payload into extraction (same shape as saved Redux state). */
export const applyFabricModalPayloadToExtraction = (prevExtraction, payload) => {
	const { fabricTypeCode, fabricTypeLabel, weight, remarks, composition, fabricTypeConstant } =
		payload || {};
	const updatedExtractionData = { ...(prevExtraction || {}) };
	const compositionLine = Array.isArray(composition)
		? composition
				.map((row) => {
					const fiberName = extractionText(row?.fiber?.name ?? row?.fiber);
					const pct = extractionText(row?.percentage);
					if (!fiberName) return '';
					return pct ? `${pct}% ${fiberName}` : fiberName;
				})
				.filter(Boolean)
				.join(', ')
		: '';

	if (fabricTypeCode || fabricTypeLabel) {
		updatedExtractionData.fabricType = fabricTypeCode || fabricTypeLabel;
		updatedExtractionData['Fabric type'] = fabricTypeLabel || fabricTypeCode;
	}
	if (weight) {
		updatedExtractionData.gsm = weight;
		updatedExtractionData['GSM/weight'] = weight;
		updatedExtractionData['GSM'] = weight;
	}
	if (remarks) {
		updatedExtractionData.description = remarks;
	}
	if (fabricTypeConstant) {
		updatedExtractionData.fabricTypeConstant = fabricTypeConstant;
	}
	if (compositionLine) {
		updatedExtractionData.Composition = compositionLine;
		updatedExtractionData['Composition (%)'] = compositionLine;
	}

	const nextBodyFabric = {
		...(typeof updatedExtractionData.bodyFabric === 'object' ? updatedExtractionData.bodyFabric : {})
	};
	if (fabricTypeCode || fabricTypeLabel) {
		const prevCon = nextBodyFabric.construction;
		const prevObj = prevCon && typeof prevCon === 'object' ? prevCon : {};
		const idFromCode = fabricTypeCode != null && fabricTypeCode !== '' ? Number(fabricTypeCode) : NaN;
		nextBodyFabric.construction = {
			...prevObj,
			...(Number.isFinite(idFromCode)
				? { id: idFromCode }
				: fabricTypeCode
					? { id: fabricTypeCode }
					: {}),
			name: fabricTypeLabel || prevObj.name || fabricTypeCode || ''
		};
	}
	if (weight) {
		nextBodyFabric.gsm = weight;
	}
	if (fabricTypeConstant) {
		nextBodyFabric.fabricTypeConstant = fabricTypeConstant;
	}
	if (composition?.length) {
		nextBodyFabric.composition = composition;
	}
	if (Object.keys(nextBodyFabric).length) {
		updatedExtractionData.bodyFabric = nextBodyFabric;
	}
	return updatedExtractionData;
};

/** Apply fabrication dropdown text ("type, composition, gsm") to extraction + bodyFabric. */
export const applyFabricDropdownToExtraction = (prevExtraction, selection) => {
	const out = { ...(prevExtraction || {}) };
	const selectedId = selection?.id != null ? Number(selection.id) : NaN;
	const hasConstructionId = Number.isFinite(selectedId) && selectedId > 0;
	const { fabricType, composition, gsm } = parseFabricationDropdown(selection?.value);

	out['Fabric type'] = selection?.value ?? '';
	if (composition) {
		out.Composition = composition;
		out['Composition (%)'] = composition;
	}
	if (gsm) {
		out.gsm = gsm;
		out.GSM = gsm;
		out['GSM/weight'] = gsm;
	}

	const nextBodyFabric = { ...(typeof out.bodyFabric === 'object' ? out.bodyFabric : {}) };
	if (fabricType || hasConstructionId) {
		nextBodyFabric.construction = {
			...(nextBodyFabric.construction && typeof nextBodyFabric.construction === 'object'
				? nextBodyFabric.construction
				: {}),
			...(hasConstructionId ? { id: selectedId } : {}),
			name: fabricType || extractionText(nextBodyFabric?.construction?.name)
		};
		if (hasConstructionId) nextBodyFabric.constructionId = selectedId;
	}
	if (gsm) nextBodyFabric.gsm = gsm;
	const compositionRows = compositionRowsFromText(composition);
	if (compositionRows.length) nextBodyFabric.composition = compositionRows;
	if (Object.keys(nextBodyFabric).length) out.bodyFabric = nextBodyFabric;
	return out;
};

export const normalizeList = (listData) => {
	if (Array.isArray(listData)) return listData;
	if (Array.isArray(listData?.data)) return listData.data;
	return [];
};

/** Build Fabric type / Composition / GSM from a comma-separated dropdown value with fallbacks. */
export const fabricLineFromDropdown = (raw, fb) => {
	const safeFb = fb != null && typeof fb === 'object' ? fb : {};
	const p = String(raw ?? '')
		.split(',')
		.map((s) => s.trim());
	const v = (i, key, altKey) => extractionText(p[i]) || safeFb[key] || safeFb[altKey];

	const typeConst = extractionText(safeFb.fabricTypeConstant);
	const gsm = v(2, 'gsmValue');
	const gsmText = extractionText(gsm);
	const alreadyHasGsmOrOz = /\b(gsm|oz)\b/i.test(gsmText);

	let gsmOut = gsm;
	if (gsmText) {
		gsmOut = alreadyHasGsmOrOz
			? gsmText
			: typeConst.toLowerCase() === 'denim'
				? `${gsmText} OZ`
				: `${gsmText} GSM`;
	}

	return [
		['Fabric type', v(0, 'constructionValue', 'fabricTypeRaw')],
		['Composition', v(1, 'compositionValue')],
		['GSM', gsmOut]
	];
};

export const isBlankMissing = (value) => !extractionText(value);

export const labelOrValue = (value, key) => {
	const t = extractionText(value);
	if (!t) return key;
	return t;
};

export const dropdownSelectionFromItem = (item) =>
	item?.id != null && item?.value != null ? { id: item.id, value: item.value } : null;
